import { Paginator } from "primereact/paginator";
import { AppDialog } from "../../../../common/components/AppDialog/AppDialog";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { MessageStatus } from "../emailLogs/models/emailLogModel";
import { CommunicationLogItem } from "../components/CommunicationLogItem";
import { CommunicationLogDialogMeta } from "../components/CommunicationLogDialogMeta";
import { PhoneScreen } from "../components/PhoneScreen";
import { useSmsLogsList } from "./hooks/useSmsLogsList";
import { DataNotice } from "../../../../common/components/dataNotice/DataNotice";
import { useAppContext } from "../../../../AppContext";
import styles from "./SmsLogsList.module.scss";

const SMS_LOGS_CUTOFF_DATE = new Date(2026, 2, 6); // 06.03.2026

interface ISmsLogsListProps {
  filterUserIds: number[] | null;
}

export const SmsLogsList = ({ filterUserIds }: ISmsLogsListProps) => {
  const { selectedStudio } = useAppContext();
  const {
    page,
    items,
    totalCount,
    isLoading,
    isCancelling,
    selectedSms,
    setSelectedSms,
    handlePageChange,
    handleCancel,
    PAGE_SIZE,
  } = useSmsLogsList({ filterUserIds });

  const showCutoffNotice =
    !selectedStudio ||
    new Date(selectedStudio.createdOn) < SMS_LOGS_CUTOFF_DATE;

  return (
    <div className={styles.root}>
      <div className={styles.root_list}>
        {isLoading &&
          Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className={styles.root_list_skeleton} />
          ))}

        {!isLoading && items.length === 0 && (
          <div className={styles.root_empty}>Няма намерени SMS съобщения.</div>
        )}

        {!isLoading &&
          items.map((item) => (
            <CommunicationLogItem
              key={item.id}
              index={item.index}
              receiverName={item.receiverName}
              receiver={item.receiverPhone}
              details={item.compiledMessage}
              status={item.status}
              createdOn={item.createdOn}
              onClick={() => setSelectedSms(item)}
            />
          ))}

        {showCutoffNotice &&
          !isLoading &&
          items.length > 0 &&
          page >= Math.ceil(totalCount / PAGE_SIZE) && (
            <DataNotice>
              Записите обхващат SMS съобщения, изпратени след{" "}
              <strong>06.03.2026 г.</strong>
            </DataNotice>
          )}
      </div>

      {totalCount > PAGE_SIZE && (
        <Paginator
          first={(page - 1) * PAGE_SIZE}
          rows={PAGE_SIZE}
          totalRecords={totalCount}
          onPageChange={handlePageChange}
          className={styles.root_paginator}
        />
      )}

      <AppDialog
        visible={!!selectedSms}
        onHide={() => setSelectedSms(null)}
        header="SMS"
        style={{ width: "90vw", maxWidth: "500px" }}
      >
        <div className={styles.dialog_content}>
          <CommunicationLogDialogMeta
            receiver={selectedSms?.receiverPhone}
            status={selectedSms?.status}
            errorMessage={selectedSms?.errorMessage}
          />

          <PhoneScreen className={styles.dialog_phone_screen}>
            <div className={styles.dialog_bubble_sent_placeholder}>
              <div className={styles.dialog_bubble_placeholder} style={{ width: "100%" }} />
              <div className={styles.dialog_bubble_placeholder} style={{ width: "70%" }} />
            </div>
            <div className={styles.dialog_bubble_received}>
              <div className={styles.dialog_bubble_placeholder} style={{ width: "80%" }} />
            </div>
            <div className={styles.dialog_bubble_sent_placeholder}>
              <div className={styles.dialog_bubble_placeholder} style={{ width: "100%" }} />
              <div className={styles.dialog_bubble_placeholder} style={{ width: "55%" }} />
            </div>
            <div className={styles.dialog_bubble_received}>
              <div className={styles.dialog_bubble_placeholder} style={{ width: "60%" }} />
              <div className={styles.dialog_bubble_placeholder} style={{ width: "100%" }} />
              <div className={styles.dialog_bubble_placeholder} style={{ width: "40%" }} />
            </div>
            <div className={styles.dialog_bubble_sent_placeholder}>
              <div className={styles.dialog_bubble_placeholder} style={{ width: "100%" }} />
            </div>
            <div className={styles.dialog_bubble}>
              {selectedSms?.compiledMessage}
            </div>
            <span className={styles.dialog_bubble_time}>
              {selectedSms && dateUtility.formatDateTime(selectedSms.createdOn)}
            </span>
          </PhoneScreen>

          <RenderWhen condition={selectedSms?.status === MessageStatus.Pending}>
            <div className={styles.dialog_actions}>
              <DefaultButton
                label="Отмени изпращане"
                design="secondary"
                onClick={handleCancel}
                loading={isCancelling}
                width="auto"
              />
            </div>
          </RenderWhen>
        </div>
      </AppDialog>
    </div>
  );
};
